/**
 * TPL-001 helpers — extract/apply «spør ved bruk» parameters from a template's
 * module_graph (globals + unlocked User Insert fields + PROD-001 product lists).
 */

import { recommendedInsertGroups } from "../calculations/nested-module-insert-fields-core";
import { sortNodesForInsertDisplay } from "../calculations/module-insert-order";
import { expandTemplateByUnits } from "./template-expand";
import { applyModuleRulesToCalculation } from "./module-rules";
import {
  defaultSelectedProductIds,
  expandProductListOntoItem,
  expandProductListOntoNode,
  PRODUCT_LIST_MODES,
  readProductListFromItem,
  readProductListFromNode,
} from "./product-list";
import { childCalculationsOf } from "./nested-calculation-tree";

const PERCENT_UNITS = new Set(["%", "percent", "pct", "prosent", "procent"]);
const NUMBER_KEY_RE =
  /(count|quantity|qty|amount|hours|rate|price|divisor|unit_count|kilometer|km\b|_m2|area)/i;

/**
 * Resolve which control the template wizard should render for a parameter.
 * Prefer explicit type/datatype/options; fall back to unit, key and value shape.
 *
 * @returns {{ fieldType: 'boolean'|'number'|'percent'|'select'|'textarea'|'text', options?: Array<{value:string,label:string}> }}
 */
export function resolveWizardFieldControl({
  type,
  datatype,
  fieldType,
  unit,
  key,
  value,
  options,
} = {}) {
  const explicit = String(type || datatype || fieldType || "")
    .trim()
    .toLowerCase();
  const unitNorm = String(unit || "")
    .trim()
    .toLowerCase();
  const keyNorm = String(key || "")
    .trim()
    .toLowerCase();
  const opts = Array.isArray(options) ? options : undefined;

  // Status/enabled is always a boolean toggle in insert + wizard UX
  // (even when the builder field catalog uses a true/false select).
  if (keyNorm === "enabled" || explicit === "boolean" || explicit === "bool") {
    return { fieldType: "boolean" };
  }

  if (opts?.length) {
    return { fieldType: "select", options: opts };
  }

  if (explicit === "select" || explicit === "enum") {
    return { fieldType: "select", options: opts || [] };
  }

  if (
    explicit === "percent" ||
    explicit === "percentage" ||
    PERCENT_UNITS.has(unitNorm) ||
    keyNorm === "percent" ||
    keyNorm.endsWith("_pct") ||
    keyNorm.includes("percent")
  ) {
    return { fieldType: "percent" };
  }

  if (
    explicit === "number" ||
    explicit === "integer" ||
    explicit === "float" ||
    explicit === "decimal"
  ) {
    return { fieldType: "number" };
  }

  if (explicit === "textarea") {
    return { fieldType: "textarea" };
  }

  if (explicit === "text" || explicit === "string") {
    return { fieldType: "text" };
  }

  if (typeof value === "boolean" || value === "true" || value === "false") {
    return { fieldType: "boolean" };
  }

  if (PERCENT_UNITS.has(unitNorm)) {
    return { fieldType: "percent" };
  }

  if (
    typeof value === "number" ||
    (typeof value === "string" &&
      value.trim() !== "" &&
      /^-?\d+([.,]\d+)?$/.test(value.trim()))
  ) {
    if (NUMBER_KEY_RE.test(keyNorm) || typeof value === "number") {
      return { fieldType: "number" };
    }
  }

  return { fieldType: "text" };
}

function normalizeWizardValue(fieldType, value) {
  if (fieldType === "boolean") {
    if (value === "" || value == null) return true;
    return value !== false && value !== "false" && value !== 0 && value !== "0";
  }
  if (value == null) return "";
  return value;
}

function coerceAnswerValue(fieldType, key, value) {
  if (fieldType === "boolean" || key === "enabled") {
    return value === true || value === "true";
  }
  if (fieldType === "number" || fieldType === "percent") {
    if (value === "" || value == null) return value;
    const n = Number(String(value).replace(",", "."));
    return Number.isFinite(n) ? n : value;
  }
  return value;
}

function productListQuestionFrom(list, id, kind, extra = {}) {
  if (!list?.options?.length || list.ask_on_use === false) return null;
  // Only linked catalog products are choosable at template-use time
  const linkedOptions = list.options.filter(
    (opt) => opt?.product_id != null && opt.product_id !== "" && Number(opt.product_id) > 0
  );
  if (!linkedOptions.length) return null;
  const selected = defaultSelectedProductIds(list);
  return {
    id,
    kind,
    fieldType: "product_list",
    mode: list.mode || PRODUCT_LIST_MODES.MULTI,
    label: list.label || extra.label || "",
    options: linkedOptions.map((opt) => ({
      value: String(opt.product_id),
      label: opt.displayTitle,
      required: Boolean(opt.required),
      sales_price: opt.sales_price,
      purchase_price: opt.purchase_price,
    })),
    value:
      list.mode === PRODUCT_LIST_MODES.SINGLE
        ? selected[0] != null
          ? String(selected[0])
          : ""
        : selected.map(String),
    ...extra,
  };
}

export function extractTemplateWizardQuestions(calcData, t) {
  const questions = [];
  const seenCalcIds = new Set();

  const walkCalc = (calc, pathPrefix = "") => {
    if (!calc) return;
    const calcKey =
      calc.id != null ? String(calc.id) : `anon:${pathPrefix || "root"}`;
    if (seenCalcIds.has(calcKey)) return;
    seenCalcIds.add(calcKey);

    const idPrefix = calc.id != null ? `calc:${calc.id}:` : pathPrefix;
    const graph = calc?.module_graph || {};

    (Array.isArray(graph.globals) ? graph.globals : []).forEach((global) => {
      if (!global?.key) return;
      if (global.ask_on_use === false) return;
      const control = resolveWizardFieldControl({
        type: global.type,
        datatype: global.datatype,
        unit: global.unit,
        key: global.key,
        value: global.value,
        options: global.options,
      });
      questions.push({
        id: `${idPrefix}global:${global.key}`,
        kind: "global",
        calcId: calc.id ?? null,
        calcTitle: calc.title || "",
        key: global.key,
        label: global.label || global.key,
        unit: global.unit || "",
        value: normalizeWizardValue(control.fieldType, global.value),
        ...control,
      });
    });

    recommendedInsertGroups(graph, t).forEach(({ node, fields }) => {
      fields.forEach((field) => {
        const raw = node.data?.[field.key];
        const control = resolveWizardFieldControl({
          type: field.type,
          unit: field.unit,
          key: field.key,
          value: raw,
          options: field.options,
        });
        questions.push({
          id: `${idPrefix}node:${node.id}:${field.key}`,
          kind: "node",
          calcId: calc.id ?? null,
          calcTitle: calc.title || "",
          nodeId: node.id,
          nodeTitle: node.title || node.id,
          key: field.key,
          label: field.label || field.key,
          unit: field.unit || "",
          value: normalizeWizardValue(control.fieldType, raw),
          ...control,
        });
      });
    });

    sortNodesForInsertDisplay(
      Array.isArray(graph.nodes) ? graph.nodes : [],
      graph.edges
    ).forEach((node) => {
      const list = readProductListFromNode(node);
      const q = productListQuestionFrom(
        list,
        `${idPrefix}product_list:node:${node.id}`,
        "product_list",
        {
          calcId: calc.id ?? null,
          nodeId: node.id,
          nodeTitle: node.title || node.id,
          label:
            list?.label ||
            node.title ||
            (typeof t === "function"
              ? t("offer.calculation.product_list.title")
              : "Produktliste"),
        }
      );
      if (q) questions.push(q);
    });

    const itemRows = Array.isArray(calc?.items) ? [...calc.items] : [];
    itemRows
      .sort(
        (a, b) =>
          (Number(a?.row_order) || 0) - (Number(b?.row_order) || 0) ||
          (Number(a?.id) || 0) - (Number(b?.id) || 0)
      )
      .forEach((item, index) => {
      const list = readProductListFromItem(item);
      const itemKey = item?.id != null ? `id:${item.id}` : `idx:${index}`;
      const q = productListQuestionFrom(
        list,
        `${idPrefix}product_list:item:${itemKey}`,
        "product_list",
        {
          calcId: calc.id ?? null,
          itemIndex: index,
          itemId: item?.id ?? null,
          label:
            list?.label ||
            item?.description ||
            (typeof t === "function"
              ? t("offer.calculation.product_list.title")
              : "Produktliste"),
        }
      );
      if (q) questions.push(q);
    });

    childCalculationsOf(calc).forEach((child, index) => {
      walkCalc(child, `${idPrefix}child:${index}:`);
    });
  };

  walkCalc(calcData);
  return questions;
}

function normalizeProductListAnswer(meta, value) {
  if (meta?.mode === PRODUCT_LIST_MODES.SINGLE) {
    if (value == null || value === "") return [];
    return [value];
  }
  if (Array.isArray(value)) return value;
  if (value == null || value === "") return [];
  return [value];
}

export function applyWizardAnswersToCalculation(calcData, answers = {}) {
  if (!calcData) return calcData;
  const questions = extractTemplateWizardQuestions(calcData, (key) => key);
  const typeById = Object.fromEntries(questions.map((q) => [q.id, q]));

  const applyToCalc = (calc, answersForCalc) => {
    if (!calc) return calc;
    const graph = {
      ...(calc.module_graph || {}),
      nodes: Array.isArray(calc.module_graph?.nodes)
        ? calc.module_graph.nodes.map((node) => ({
            ...node,
            data: { ...(node.data || {}) },
          }))
        : [],
      globals: Array.isArray(calc.module_graph?.globals)
        ? calc.module_graph.globals.map((g) => ({ ...g }))
        : [],
      edges: calc.module_graph?.edges || [],
    };

    let items = Array.isArray(calc.items)
      ? calc.items.map((item) => ({ ...item }))
      : [];

    items = items.map((item) => {
      const list = readProductListFromItem(item);
      if (!list || list.ask_on_use !== false) return item;
      return expandProductListOntoItem(item, defaultSelectedProductIds(list));
    });
    graph.nodes = graph.nodes.map((node) => {
      const list = readProductListFromNode(node);
      if (!list || list.ask_on_use !== false) return node;
      return expandProductListOntoNode(node, defaultSelectedProductIds(list));
    });

    Object.entries(answersForCalc).forEach(([id, value]) => {
      const meta = typeById[id] || typeById[id.replace(/^calc:[^:]+:/, "")];
      const coerced = coerceAnswerValue(meta?.fieldType, meta?.key, value);
      const localId = id.replace(/^calc:[^:]+:/, "");

      if (localId.startsWith("product_list:node:")) {
        const nodeId = localId.slice("product_list:node:".length);
        const selected = normalizeProductListAnswer(meta, value);
        graph.nodes = graph.nodes.map((node) =>
          node.id === nodeId ? expandProductListOntoNode(node, selected) : node
        );
        return;
      }
      if (localId.startsWith("product_list:item:")) {
        const key = localId.slice("product_list:item:".length);
        const selected = normalizeProductListAnswer(meta, value);
        items = items.map((item, index) => {
          const match =
            (key.startsWith("id:") && String(item.id) === key.slice(3)) ||
            (key.startsWith("idx:") && String(index) === key.slice(4)) ||
            (meta?.itemIndex === index);
          return match ? expandProductListOntoItem(item, selected) : item;
        });
        return;
      }

      if (localId.startsWith("global:")) {
        const key = localId.slice("global:".length);
        graph.globals = graph.globals.map((global) =>
          global.key === key ? { ...global, value: coerced } : global
        );
        return;
      }
      if (localId.startsWith("node:")) {
        const rest = localId.slice("node:".length);
        const sep = rest.lastIndexOf(":");
        if (sep < 0) return;
        const nodeId = rest.slice(0, sep);
        const fieldKey = rest.slice(sep + 1);
        graph.nodes = graph.nodes.map((node) => {
          if (node.id !== nodeId) return node;
          return {
            ...node,
            data: { ...(node.data || {}), [fieldKey]: coerced },
          };
        });
      }
    });

    return {
      ...calc,
      items,
      module_enabled:
        Boolean(calc.module_enabled) || graph.nodes.length > 0,
      module_graph: graph,
    };
  };

  const partitionAnswers = (calc) => {
    const own = {};
    Object.entries(answers).forEach(([id, value]) => {
      const m = String(id).match(/^calc:([^:]+):(.*)$/);
      if (m) {
        if (calc?.id != null && String(calc.id) === m[1]) {
          own[id] = value;
        }
        return;
      }
      // Legacy unprefixed ids apply to the root only.
      if (calc === calcData) own[id] = value;
    });
    return own;
  };

  const walkApply = (calc) => {
    if (!calc) return calc;
    let next = applyToCalc(calc, partitionAnswers(calc));
    next = {
      ...next,
      nested_calculations: (
        Array.isArray(next.nested_calculations) ? next.nested_calculations : []
      ).map(walkApply),
      items: (Array.isArray(next.items) ? next.items : []).map((item) => {
        if (!item?.nested_calculation && item?.nested_calculation_id == null) {
          return item;
        }
        const nested = walkApply(
          item.nested_calculation || { id: item.nested_calculation_id }
        );
        return { ...item, nested_calculation: nested };
      }),
    };
    return next;
  };

  return expandTemplateByUnits(
    applyModuleRulesToCalculation(walkApply(calcData))
  );
}

export function templateNeedsWizard(calcData, t) {
  return extractTemplateWizardQuestions(calcData, t).length > 0;
}
